import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { profile, loadProfile, getBodyMap } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { backLink } from "../components/common.js";
import { spinner, errorState, emptyState, toast, openModal, confirmDialog } from "../components/ui.js";
import { lineChart } from "../components/Chart.js";
import { toDisplayWeight, toStorageKg, weightLabel, fmtDateTime, parseDate } from "../lib/format.js";
import { bmi, bmiCategory, navyBodyFat, ffmi } from "../lib/health.js";
import { isInMorningWeightWindow } from "../lib/bodyweight.js";

export default function Weight() {
  const root = h("div", { class: "screen" });
  let data = null;
  let measurements = null;
  load(true);

  async function load(maybeOpen = false) {
    setChildren(root, backLink("Progress", "/history"), spinner());
    try {
      const [bw, ms] = await Promise.all([api.bodyweight(), api.measurements().catch(() => null)]);
      data = bw;
      measurements = ms;
      render();
      // Opened from a reminder notification (/weight?log=1) → jump straight to logging.
      if (maybeOpen && new URLSearchParams(location.search).get("log")) {
        history.replaceState({}, "", "/weight");
        openLogModal();
      }
    } catch (e) {
      setChildren(root, backLink("Progress", "/history"), errorState(e.message, () => load()));
    }
  }

  // Weight (kg) closest to `days` ago, for the change chips.
  function around(days) {
    if (!data.entries.length) return null;
    const target = Date.now() - days * 86400000;
    let pick = null;
    for (const e of data.entries) {
      const t = parseDate(e.logged_at)?.getTime() ?? 0;
      if (t <= target) pick = e; // entries are ascending → last one before target
    }
    return (pick || data.entries[0]).weight_kg;
  }

  function changeChip(label, days) {
    if (data.current_kg == null || data.entries.length < 2) return null;
    const past = around(days);
    if (past == null) return null;
    const deltaKg = data.current_kg - past;
    const d = Number(toDisplayWeight(Math.abs(deltaKg))) || 0;
    if (d === 0) return chip(label, "± 0", "var(--muted)");
    const up = deltaKg > 0;
    return chip(label, `${up ? "▲" : "▼"} ${d}`, up ? "var(--warn)" : "var(--success)");
  }

  function chip(label, value, color) {
    return h("div", { class: "card", style: { flex: "1", textAlign: "center", padding: "10px" } },
      h("div", { style: { fontWeight: 800, color } }, value),
      h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "2px" } }, label)
    );
  }

  function render() {
    const cur = data.current_kg;
    const points = data.entries.map((e) => ({ x: e.logged_at, y: Number(toDisplayWeight(e.weight_kg)) }));

    setChildren(root,
      backLink("Progress", "/history"),
      h("div", { class: "row-between", style: { alignItems: "center" } },
        h("h1", { style: { margin: "4px 0" } }, "Bodyweight"),
        data.logged_today ? h("span", { class: "badge", style: { background: "var(--success)", color: "#fff" } }, "Logged today") : null
      ),

      h("div", { class: "card center mt", style: { padding: "20px" } },
        cur != null
          ? h("div", null,
              h("div", { style: { fontSize: "2.6rem", fontWeight: 800, lineHeight: 1 } }, toDisplayWeight(cur), h("small", { class: "dim", style: { fontSize: "1rem", marginLeft: "6px" } }, weightLabel())),
              h("div", { class: "dim", style: { marginTop: "4px", fontSize: ".82rem" } }, data.entries.length ? `Last logged ${fmtDateTime(data.entries[data.entries.length - 1].logged_at)}` : "")
            )
          : h("p", { class: "dim", style: { margin: 0 } }, "No weight logged yet.")
      ),

      (data.current_kg != null && data.entries.length >= 2)
        ? h("div", { class: "hstack mt", style: { gap: "10px" } }, changeChip("7-day", 7), changeChip("30-day", 30))
        : null,

      bodyCompositionSection(),

      bmiSection(),

      goalSection(),

      h("button", { class: "btn btn-primary btn-block btn-lg mt", onClick: openLogModal },
        icon("plus", { size: 20 }), data.logged_today ? "Update today's weight" : "Log weight"),

      data.entries.length >= 2
        ? h("div", null,
            h("div", { class: "section-title" }, "Trend"),
            h("div", { class: "card" }, lineChart(points, {
              format: (v) => `${v} ${weightLabel()}`,
              goal: data.goal_kg != null ? Number(toDisplayWeight(data.goal_kg)) : null,
              xScale: "time",
            }))
          )
        : null,

      data.entries.length
        ? h("div", null,
            h("div", { class: "section-title" }, "History"),
            h("div", { class: "card" }, ...[...data.entries].reverse().map(entryRow))
          )
        : emptyState({ icon: "weight", title: "Track your weight", message: "Log your bodyweight to see your trend over time. The app can also remind you daily." })
    );
  }

  // FFMI & Body Fat % from bodyweight + measurements (waist, neck, hips).
  function bodyCompositionSection() {
    if (data.current_kg == null) return null;
    const heightCm = profile()?.height_cm;
    const gender = getBodyMap();
    const sites = measurements?.sites || [];
    const waist = sites.find((s) => s.site === "waist")?.current_cm;
    const neck = sites.find((s) => s.site === "neck")?.current_cm;
    const hips = sites.find((s) => s.site === "hips")?.current_cm;

    if (!heightCm || !waist || !neck) {
      return h("div", { class: "card mt", style: { padding: "14px 16px" } },
        h("div", { class: "row-between", style: { alignItems: "center" } },
          h("div", null,
            h("div", { style: { fontWeight: 600, fontSize: ".9rem" } }, "FFMI & Body Fat %"),
            h("div", { class: "dim", style: { fontSize: ".78rem", marginTop: "2px" } }, "Log waist and neck in Measurements to track muscle mass and body fat.")
          ),
          h("button", { class: "btn btn-ghost btn-sm", onClick: () => navigate("/measurements") }, "Measurements")
        )
      );
    }

    const bf = navyBodyFat({ gender, heightCm, waistCm: waist, neckCm: neck, hipsCm: hips });
    const f = bf != null ? ffmi({ weightKg: data.current_kg, heightCm, bodyFatPct: bf }) : null;
    if (!f || bf == null) return null;

    return h("div", { class: "card mt", style: { padding: "16px" } },
      h("div", { class: "section-title", style: { margin: "0 0 10px" } }, "Body composition"),
      h("div", { class: "stat-grid", style: { gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" } },
        h("div", { class: "stat", style: { padding: "12px" } },
          h("div", { class: "num accent", style: { fontSize: "1.7rem" } }, String(f.normalized)),
          h("div", { class: "lbl" }, "Normalized FFMI")
        ),
        h("div", { class: "stat", style: { padding: "12px" } },
          h("div", { class: "num flame", style: { fontSize: "1.7rem" } }, `${bf}%`),
          h("div", { class: "lbl" }, "Est. body fat")
        )
      ),
      h("div", { class: "dim mt", style: { fontSize: ".76rem", lineHeight: "1.45" } },
        `Lean mass: ~${toDisplayWeight(f.leanMassKg)} ${weightLabel()} · US Navy circumference method.`)
    );
  }

  // BMI from current weight + the profile's stored height. Contextualized for lifters.
  function bmiSection() {
    if (data.current_kg == null) return null;
    const heightCm = profile()?.height_cm;
    if (!heightCm) {
      return h("button", { class: "btn btn-ghost btn-block mt", onClick: () => navigate("/profile") },
        icon("ruler", { size: 16 }), "Add your height for BMI & FFMI");
    }
    const value = bmi(data.current_kg, heightCm);
    if (value == null) return null;
    const cat = bmiCategory(value);
    return h("div", { class: "card mt", style: { padding: "14px 16px" } },
      h("div", { class: "row-between", style: { alignItems: "center" } },
        h("div", { class: "hstack", style: { gap: "10px", alignItems: "baseline" } },
          h("span", { class: "dim", style: { fontSize: ".82rem", fontWeight: 600 } }, "BMI"),
          h("span", { style: { fontWeight: 800, fontSize: "1.3rem" } }, value.toFixed(1))
        ),
        h("span", { class: "badge", style: { background: "var(--surface-2)", color: "var(--text-2)" } }, cat?.label || "")
      ),
      h("p", { class: "dim", style: { fontSize: ".72rem", margin: "6px 0 0", lineHeight: "1.4" } },
        "Population screening guide. BMI does not distinguish muscle from fat mass; for strength trainees, FFMI and waist measurements are more informative.")
    );
  }

  function goalSection() {
    const goal = data.goal_kg;
    if (goal == null) {
      return h("button", { class: "btn btn-ghost btn-block mt", onClick: openGoalModal }, icon("trophy", { size: 16 }), "Set a goal weight");
    }
    let detail = "Goal set";
    if (data.current_kg != null) {
      const diffDisp = Number(toDisplayWeight(Math.abs(data.current_kg - goal))) || 0;
      detail = diffDisp < 0.05 ? "Goal reached 🎉"
        : data.current_kg > goal ? `${diffDisp} ${weightLabel()} to lose`
        : `${diffDisp} ${weightLabel()} to gain`;
    }
    return h("div", { class: "card card-tap row-between mt", style: { alignItems: "center" }, onClick: openGoalModal },
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
        h("span", { style: { fontSize: "1.1rem" } }, "🎯"),
        h("div", null,
          h("div", { style: { fontWeight: 700 } }, `Goal ${toDisplayWeight(goal)} ${weightLabel()}`),
          h("div", { class: "dim", style: { fontSize: ".8rem", marginTop: "2px" } }, detail)
        )
      ),
      icon("edit", { size: 16, className: "icon muted" })
    );
  }

  function openGoalModal() {
    const prefill = data.goal_kg != null ? toDisplayWeight(data.goal_kg) : "";
    const input = h("input", { class: "input", type: "number", inputmode: "decimal", step: weightLabel() === "kg" ? "0.05" : "1", value: prefill, placeholder: `Target in ${weightLabel()}` });
    const save = async (clear) => {
      let kg = null;
      if (!clear) {
        kg = toStorageKg(input.value);
        if (kg == null || kg <= 0) { toast("Enter a valid weight", "warn"); return; }
      }
      m.close();
      try {
        await api.updateMe({ weight_goal_kg: clear ? null : kg });
        await loadProfile().catch(() => {});
        toast(clear ? "Goal cleared" : "Goal saved", "success");
        await load();
      } catch (e) { toast(e.message, "error"); }
    };
    const m = openModal({
      title: "Goal weight",
      content: h("div", null,
        h("div", { class: "field" }, h("label", null, `Target weight (${weightLabel()})`), input),
        h("div", { class: "btn-row mt" },
          data.goal_kg != null ? h("button", { class: "btn btn-ghost", onClick: () => save(true) }, "Clear") : null,
          h("button", { class: "btn btn-primary grow", onClick: () => save(false) }, "Save")
        )
      ),
    });
    setTimeout(() => input.focus(), 50);
  }

  function entryRow(e) {
    const contexts = [
      e.source === "session" ? "after a workout" : null,
      e.is_first_morning ? "first-morning, post-void" : null,
    ].filter(Boolean);
    return h("div", { class: "row-between", style: { padding: "9px 0", alignItems: "center" } },
      h("div", null,
        h("div", { style: { fontWeight: 600 } }, `${toDisplayWeight(e.weight_kg)} ${weightLabel()}`),
        h("div", { class: "dim", style: { fontSize: ".78rem" } }, fmtDateTime(e.logged_at) + (contexts.length ? ` · ${contexts.join(" · ")}` : ""))
      ),
      h("button", { class: "icon-btn ghost danger", "aria-label": "Delete entry", onClick: () => removeEntry(e) }, icon("trash", { size: 16 }))
    );
  }

  function openLogModal() {
    const prefill = data.current_kg != null ? toDisplayWeight(data.current_kg) : "";
    const input = h("input", { class: "input", type: "number", inputmode: "decimal", step: weightLabel() === "kg" ? "0.05" : "1", value: prefill, placeholder: `Weight in ${weightLabel()}` });
    const wakeTime = profile()?.wake_time || "";
    const morning = h("input", { type: "checkbox", checked: isInMorningWeightWindow(wakeTime) });
    const save = async () => {
      const kg = toStorageKg(input.value);
      if (kg == null || kg <= 0) { toast("Enter a valid weight", "warn"); return; }
      m.close();
      try {
        await api.logBodyweight({ weight_kg: kg, source: "manual", is_first_morning: morning.checked });
        await loadProfile().catch(() => {});
        toast("Weight logged", "success");
        await load();
      } catch (e) {
        toast(e.message, "error");
      }
    };
    const m = openModal({
      title: "Log bodyweight",
      content: h("div", null,
        h("div", { class: "field" }, h("label", null, `Today's weight (${weightLabel()})`), input),
        h("label", { class: "hstack", style: { gap: "10px", alignItems: "flex-start", marginTop: "12px", cursor: "pointer" } },
          morning,
          h("span", null,
            h("span", { style: { display: "block", fontWeight: 600 } }, "First-morning, post-void body weight"),
            h("span", { class: "dim", style: { display: "block", fontSize: ".78rem", marginTop: "2px" } },
              wakeTime
                ? `Automatically selected within 3 hours of your ${wakeTime} wake time.`
                : "Set a usual wake-up time in Settings to select this automatically.")
          )
        ),
        h("button", { class: "btn btn-primary btn-block mt", onClick: save }, "Save")
      ),
    });
    setTimeout(() => input.focus(), 50);
  }

  async function removeEntry(e) {
    const ok = await confirmDialog({ title: "Delete entry?", message: `Remove the ${toDisplayWeight(e.weight_kg)} ${weightLabel()} entry from ${fmtDateTime(e.logged_at)}?`, confirmText: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.deleteBodyweight(e.id);
      await loadProfile().catch(() => {});
      await load();
    } catch (err) {
      toast(err.message, "error");
    }
  }

  return root;
}
